

import { StrictMode, Suspense, lazy } from 'react';
import { render as DOMrender } from 'react-dom';
import { bindAll, IOCContainer, Container } from './ioc';
import { injectRouter } from './interface/route';
import { injectConfigRoute } from './interface/config';
import { injectServiceWorker } from './interface/serviceworker';
import { injectENV, injectLog } from './interface/util';
import { bindContainer, IContainer } from './interface/container';
import type { ReportHandler } from 'web-vitals';

const LOADING_STYLE_NAME = 'OnBootLoadingStyle';

function loadContainerBindings(container: IContainer): Promise<void> {
    return bindAll(container);
}

function startServiceWorker(container: IContainer): Promise<void> {
    return new Promise(async function(resolve, reject) {
        try {
            const isProduction = (process.env.NODE_ENV === 'production');
            const swEnableOverride = Boolean((process.env.REACT_APP_SWFORCE) || 'false'); /* for testing, run `yarn startsw` to force the Service Worker to run in development */
            const hasServiceWorker = 'serviceWorker' in navigator;
            const env = injectENV(container);
            const swEnabled = env.getBoolean('REACT_APP_SW_ENABLED', true); /* if missing assume YES, otherwise allow for ENV to disable service-workers (Agnelli) */

            /* Our service worker won't work if PUBLIC_URL is on a different origin
                from what our page is served on. This might happen if a CDN is used to
                serve assets; see https://github.com/facebook/create-react-app/issues/2374 */
            const urlIsAtOrigin = (new URL(process.env.PUBLIC_URL, window.location.href)).origin === window.location.origin;

            if ( !swEnabled ) {}
            else if ( !hasServiceWorker ) {}
            else if ( !urlIsAtOrigin) {}
            else if ( isProduction || swEnableOverride ) {
                const sw = await injectServiceWorker(container);
                sw.start();
            }

            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

function startRenderer(container: IContainer): Promise<void> {
    return new Promise(async function(resolve, reject) {
        try {
            const router = injectRouter(container);
            const config = injectConfigRoute(container);
        
            const allRoutes = router.allRoutes();
        
            const App = lazy(() => import('./app'));
            const LoadingView = (
                <div className={LOADING_STYLE_NAME}>Loading..</div>
            );

            DOMrender(
                <StrictMode>
                    <IOCContainer container={container}>
                        <Suspense fallback={LoadingView}>
                            <App basename={config.basename} routes={allRoutes} />
                        </Suspense>
                    </IOCContainer>
                </StrictMode>,
                document.getElementById('root')
            );

            resolve();
        } catch (error) {
            reject(error);
        }
    })
}

/* start */
const container = Container();

bindContainer(container);

loadContainerBindings(container)
    .then(_ => startServiceWorker(container))
    .then(_ => startRenderer(container))
    .then(_ => {
        injectLog(container).milestone('App running');
    })
    .catch(error => {
        injectLog(container).error(`Failed to start app: ${JSON.stringify(error)}`);
    });

/* If you want to start measuring performance in your app, pass a function
   to log results (for example: reportWebVitals(console.log))
   or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals */
const isProduction = (process.env.NODE_ENV === 'production'); /* disabled for production builds */
if ( !isProduction) {
    const reportWebVitals = (onPerfEntry?: ReportHandler) => {
        if (onPerfEntry && onPerfEntry instanceof Function) {
            import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
                getCLS(onPerfEntry);  
                getFID(onPerfEntry);
                getFCP(onPerfEntry);
                getLCP(onPerfEntry);
                getTTFB(onPerfEntry);
            });
        }
    }

    reportWebVitals();
}

